/**
 * validator.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Template SDK validation engine.
 * Audits template manifests to ensure structural validity and prevent runtime errors.
 */

export function validateTemplate(manifest) {
  const errors = [];
  const warnings = [];

  if (!manifest) {
    errors.push('Manifest object is null or undefined.');
    return { valid: false, errors, warnings };
  }

  const identity = manifest.name || manifest.id || 'unknown';

  // 1. Check required properties
  if (!manifest.id) {
    errors.push('Template manifest is missing required "id" field.');
  }
  if (!manifest.name) {
    errors.push(`Template [${identity}] is missing required "name" field.`);
  }
  if (!manifest.layout) {
    errors.push(`Template [${identity}] is missing required "layout" string.`);
  }
  if (!manifest.theme) {
    errors.push(`Template [${identity}] is missing required "theme" string.`);
  }
  if (!manifest.density) {
    errors.push(`Template [${identity}] is missing required "density" string.`);
  }

  // 2. Verify layouts configuration
  if (manifest.layout && (!manifest.layouts || typeof manifest.layouts !== 'object')) {
    errors.push(`Template [${identity}] lacks layout slots mappings configuration under "layouts" key.`);
  }

  // 3. Verify section elements order
  if (!Array.isArray(manifest.sectionOrder) || manifest.sectionOrder.length === 0) {
    errors.push(`Template [${identity}] must specify a non-empty "sectionOrder" list.`);
  }

  // 4. Verify component mappings
  if (!manifest.components || typeof manifest.components !== 'object') {
    errors.push(`Template [${identity}] is missing required components registry under "components" key.`);
  }

  // 5. Verify capability configuration
  if (!manifest.capabilities || typeof manifest.capabilities !== 'object') {
    errors.push(`Template [${identity}] must define a valid "capabilities" flags mapping object.`);
  }

  // 6. Verify print variables
  if (!manifest.print || typeof manifest.print !== 'object') {
    warnings.push(`Template [${identity}] is missing print guidelines (safe margins, avoid-break list).`);
  }

  // Print results to developer console if warnings or errors exist
  if (errors.length > 0) {
    console.error(`[SDK VALIDATOR] Template "${identity}" failed validation:`, errors);
  } else if (warnings.length > 0) {
    console.warn(`[SDK VALIDATOR] Template "${identity}" loaded with warnings:`, warnings);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

export default validateTemplate;
